import { Action } from 'redux-actions';

import { Segment } from '../../schemas/Segment';
import {
	SET_LOCATION,
	SET_MULTI_CITY,
	SET_SEGMENTS,
	SetLocationAction,
	SetSegmentsAction,
	SWAP_LOCATIONS,
	SWAP_MULTI_LOCATIONS
} from './actions';
import {
	ADD_SEGMENT,
	CLEAR_DATES,
	REMOVE_SEGMENT,
	RemoveSegmentAction,
	ClearDatesAction,
	SET_DATE,
	SET_DATE_MULTI_CITY,
	SetDateAction,
	SetDateMultiAction,
	TOGGLE_MULTI_CITY
} from './actions';

export const MIN_SEGMENTS_NUMBER = 1;

export interface SegmentsState {
	simpleRouteSegment: Segment;
	multiCitySegments: Segment[];
	isMultiCity: boolean;
}

export const defaultState: SegmentsState = {
	simpleRouteSegment: {
		departure: null,
		arrival: null,
		date: null,
		dateBack: null
	},
	multiCitySegments: [],
	isMultiCity: false
};

type SegmentsActions =
	| Action<null>
	| Action<boolean>
	| SetDateAction
	| SetDateMultiAction
	| SetLocationAction
	| RemoveSegmentAction
	| ClearDatesAction
	| SetSegmentsAction;

export const segmentsReducer = (state: SegmentsState = defaultState, action: SegmentsActions): SegmentsState => {
	switch (action.type) {
		case TOGGLE_MULTI_CITY:
			const multiCitySegments = [];

			if (!state.isMultiCity && !state.multiCitySegments.length) {
				multiCitySegments.push({ ...state.simpleRouteSegment, dateBack: null });
			}

			return { ...state, isMultiCity: !state.isMultiCity, multiCitySegments: [...multiCitySegments] };

		case SET_MULTI_CITY:
			const multiCitySegmentsArray = state.multiCitySegments;
			const value = (action as Action<boolean>).payload;

			if (value && !state.multiCitySegments.length) {
				multiCitySegmentsArray.push({ ...state.simpleRouteSegment, dateBack: null });
			}

			return {
				...state,
				isMultiCity: value,
				multiCitySegments: [...multiCitySegmentsArray]
			};

		case ADD_SEGMENT:
			const numOfSegments = state.multiCitySegments.length;
			const newMultiCitySegments: SegmentsState['multiCitySegments'] = [];

			// Create a new segment object and use the airport of arrival from the previous segment
			// as the departure airport of the new segment.
			newMultiCitySegments.push({
				departure: state.multiCitySegments[numOfSegments - 1]
					? state.multiCitySegments[numOfSegments - 1].arrival
					: null,
				arrival: null,
				date: null,
				dateBack: null
			});

			return { ...state, multiCitySegments: [...state.multiCitySegments, ...newMultiCitySegments] };

		case REMOVE_SEGMENT:
			if (state.multiCitySegments.length > MIN_SEGMENTS_NUMBER) {
				const previousMultiCitySegments = [...state.multiCitySegments];
				previousMultiCitySegments.splice((action as RemoveSegmentAction).payload, 1);

				return {
					...state,
					multiCitySegments: [...previousMultiCitySegments]
				};
			}

			return { ...state };

		case SET_DATE:
			if ((action as SetDateAction).payload.isBackDate) {
				return {
					...state,
					simpleRouteSegment: {
						...state.simpleRouteSegment,
						dateBack: (action as SetDateAction).payload.date
					}
				};
			} else {
				return {
					...state,
					simpleRouteSegment: { ...state.simpleRouteSegment, date: (action as SetDateAction).payload.date }
				};
			}

		case SET_DATE_MULTI_CITY:
			const oldMultiCitySegments = [...state.multiCitySegments];
			const currentMultiCitySegment = oldMultiCitySegments[(action as SetDateMultiAction).payload.segmentIndex];

			oldMultiCitySegments[(action as SetDateMultiAction).payload.segmentIndex] = {
				...currentMultiCitySegment,
				...{ date: (action as SetDateMultiAction).payload.date }
			};

			return {
				...state,
				multiCitySegments: oldMultiCitySegments
			};

		case CLEAR_DATES:
			if (state.isMultiCity) {
				const segmentIndex = (action as ClearDatesAction).payload;
				const newDateState = [...state.multiCitySegments];
				newDateState[segmentIndex].date = null;

				return {
					...state,
					multiCitySegments: newDateState
				};
			}

			const newDateState = {
				date: state.simpleRouteSegment.date,
				dateBack: state.simpleRouteSegment.dateBack
			};

			if (state.simpleRouteSegment.dateBack) {
				newDateState.dateBack = null;
			} else if (state.simpleRouteSegment.date) {
				newDateState.date = null;
			}

			return {
				...state,
				simpleRouteSegment: {
					...state.simpleRouteSegment,
					...newDateState
				}
			};

		case SET_LOCATION:
			// Is simple route
			if (typeof (action as SetLocationAction).payload.segmentIndex === 'undefined') {
				return {
					...state,
					simpleRouteSegment: {
						...state.simpleRouteSegment,
						[(action as SetLocationAction).payload.type]: (action as SetLocationAction).payload.location
					}
				};
			} else {
				const currentMultiCitySegment = {
					...state.multiCitySegments[(action as SetLocationAction).payload.segmentIndex],
					[(action as SetLocationAction).payload.type]: { ...(action as SetLocationAction).payload.location }
				};

				const oldMultiCitySegments = [...state.multiCitySegments];

				oldMultiCitySegments[(action as SetLocationAction).payload.segmentIndex] = {
					...currentMultiCitySegment
				};

				return {
					...state,
					multiCitySegments: [...oldMultiCitySegments]
				};
			}

		case SWAP_LOCATIONS:
			return {
				...state,
				simpleRouteSegment: {
					...state.simpleRouteSegment,
					arrival: state.simpleRouteSegment.departure ? { ...state.simpleRouteSegment.departure } : null,
					departure: state.simpleRouteSegment.arrival ? { ...state.simpleRouteSegment.arrival } : null
				}
			};

		case SWAP_MULTI_LOCATIONS:
			return {
				...state,
				multiCitySegments: state.multiCitySegments.map((segment, index) => {
					if (index === (action as Action<number>).payload) {
						return {
							...segment,
							departure: segment.arrival ? { ...segment.arrival } : null,
							arrival: segment.departure ? { ...segment.departure } : null
						};
					}

					return segment;
				})
			};

		case SET_SEGMENTS:
			return (action as SetSegmentsAction).payload;

		default:
			return state;
	}
};
